// src/services/impersonationService.ts
//
// Client-side wrapper around the `impersonation-session` Supabase Edge
// Function (issue #19).
//
// Why this exists: previously AuthContext decided client-side whether an
// admin could impersonate (reading role from localStorage — forgeable)
// and never audited it. This wrapper routes the start/stop decision
// through the edge function, which verifies the caller's real JWT is an
// active admin and writes an append-only audit row before returning the
// target's real staff profile.

import { supabase } from '../supabase/config';
import type { UserRole } from '../types/roles';

export interface ImpersonationTarget {
  id: string;
  email: string;
  name?: string;
  role?: UserRole;
  department?: string;
  curriculumArea?: string;
  staffCode?: string;
  staffId?: string;
  fte?: number;
  status?: 'active' | 'inactive' | 'pending' | 'suspended';
  permissions?: string[];
}

async function callImpersonation<T>(body: Record<string, unknown>): Promise<T> {
  // supabase.functions.invoke attaches the active session's bearer token,
  // so the edge function can verify caller identity server-side.
  const { data, error } = await supabase.functions.invoke('impersonation-session', {
    body,
  });
  if (error) {
    // Surface the edge function's error message where available so the
    // admin sees e.g. "Cannot impersonate inactive user".
    // supabase-js wraps non-2xx responses; the JSON body is on context.
    let message = error.message || 'Impersonation request failed';
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // fall through with the generic message
    }
    throw new Error(message);
  }
  return data as T;
}

export const impersonationService = {
  /**
   * Server-verified start of impersonation. Throws if the caller is not
   * an active admin or the target is invalid/inactive. Returns the
   * target's real staff profile (with a real UUID id).
   */
  async start(targetEmail: string): Promise<ImpersonationTarget> {
    const res = await callImpersonation<{ target: ImpersonationTarget }>({
      action: 'start',
      targetEmail,
    });
    return res.target;
  },

  /**
   * Records the end of an impersonation session. Best-effort audit; does
   * not throw on audit failure so the admin can always return to safety.
   */
  async stop(targetEmail?: string): Promise<void> {
    try {
      await callImpersonation<{ success: true }>({
        action: 'stop',
        targetEmail,
      });
    } catch (e) {
      // Stopping must always succeed locally even if the audit write
      // fails; log and move on.
      console.error('impersonationService.stop: audit call failed', e);
    }
  },
};
